import React, { useMemo} from 'react';
import { useNavigate} from 'react-router-dom';
import { useApiQuery, useAuth, useAlarms, resolveActiveLocale} from '@tyndo/hooks';
import { Button, Card, CardContent} from '@tyndo/ui-kit';
import {
 ChevronRight,
 Package,
 AlertTriangle,
 ClipboardList,
 TrendingUp,
 type LucideIcon,
} from 'lucide-react';
import { icons} from 'lucide-react';
import {
 viewRegistry,
 moduleManifestRegistry,
 type ModuleManifestEntry,
} from '../registries';
import { inferIsaGroup, ISA_GROUP_LABEL, ISA_GROUP_ORDER} from '../layout/isaGroups';
import { viewSlugForUrl } from '../utils/urlHelpers';
import { useModuleAccess } from '../layout/ModuleAccessContext';
import { filterByModuleAccess } from '../layout/entitlementGate';
import { useAdaptiveShell } from '../adaptive/useAdaptiveShell';
import { adaptDashboardSurfaces } from '../adaptive/adaptiveNav';
import { Sparkles} from 'lucide-react';

/**
 * /browse — the canonical"all modules" catalogue. Derives entirely from the
 * gateway's `/api/v1/ui/manifest` so every module Tyndo ships shows up
 * automatically. Grouped by ISA-95 layer to match the sidebar.
 *
 * Plant Canvas at `/` is the *operational* home; this is the *catalogue*.
 */
export default function HomePage() {
 const navigate = useNavigate();
 const { user} = useAuth();
 const alarms = useAlarms() ?? [];

 // Module list — derived from the manifest, NOT hardcoded. This is the raw
 // CATALOGUE and must never be rendered directly: everything below renders
 // `permittedModules`.
 const modules = moduleManifestRegistry.list();

 // Entitlement-bounded permitted modules, straight from the shell's single
 // fail-closed gate (AppShell owns it; this page only consumes it). While the
 // gate is loading or unavailable the permitted set is EMPTY and the page
 // renders an explicit state instead of the catalogue.
 const moduleAccess = useModuleAccess();
 const permittedModules = useMemo(
 () => filterByModuleAccess(moduleAccess, modules, (m) => m.moduleId),
 [modules, moduleAccess]
 );

 const permittedModuleIds = useMemo(
 () => permittedModules.map((m) => m.moduleId),
 [permittedModules]
 );
 const permittedModuleIdSet = useMemo(
 () => new Set(permittedModuleIds),
 [permittedModuleIds]
 );
 const adaptive = useAdaptiveShell(permittedModuleIds);

 // Adaptive dashboard: default the landing's featured row to the persona's
 // most-used PERMITTED surfaces. `adaptDashboardSurfaces` ranks only within the
 // permitted set and returns the honest default order when there's no signal.
 const mostUsed = useMemo(() => {
 if (!adaptive.active) return [];
 const ranked = adaptDashboardSurfaces(
 permittedModules.map((m) => ({ key: m.moduleId, mod: m})),
 adaptive.counts,
 adaptive.active,
 4
 );
 // Only feature surfaces that actually have a recorded count — never pad the
 // row with unused modules dressed up as "most used".
 return ranked.filter((r) => (adaptive.counts[r.key] ?? 0) > 0).map((r) => r.mod);
}, [permittedModules, adaptive.counts, adaptive.active]);

 // Group PERMITTED modules by ISA-95 layer for ordered presentation. This
 // iterated the full `modules` catalogue before, which is precisely how an
 // unentitled module reached the browse grid (and its keyboard tab order)
 // despite `permittedModules` being computed above.
 const grouped = useMemo(() => {
 const buckets = new Map<string, ModuleManifestEntry[]>();
 ISA_GROUP_ORDER.forEach((g) => buckets.set(g, []));
 permittedModules.forEach((m) => {
 const group = inferIsaGroup(m.moduleId, m);
 buckets.get(group)?.push(m);
});
 // Sort each bucket alphabetically
 for (const list of buckets.values()) {
 list.sort((a, b) => a.name.localeCompare(b.name));
}
 return buckets;
}, [permittedModules]);

 // Quick stats
 const { data: scadaOverview} = useApiQuery<{
 equipmentCount?: number;
 tagCount?: number;
} | null>('/scada/overview');
 const { data: workOrders} = useApiQuery<Array<{ status: string}> | null>(
'/production/workorders'
 );
 const equipmentCount = scadaOverview?.equipmentCount;
 const activeAlarmCount = alarms.length;
 const activeWorkOrderCount = workOrders?.filter(
 (w) => w.status ==='Released' || w.status ==='InProgress'
 ).length;

 const handleModuleClick = (mod: ModuleManifestEntry) => {
 // Defence in depth: a card can only be rendered from `permittedModules`,
 // but never navigate on a stale card if the gate moved underneath it.
 if (!moduleAccess.permittedModuleIds.has(mod.moduleId)) return;
 // Record the open as the honest usage signal driving adaptive ordering.
 adaptive.recordUse(mod.moduleId);
 // Try to resolve the module's default view
 const views = viewRegistry.listViews().filter((v) => v.moduleId === mod.moduleId);
 const defaultView = views.find((v) => v.isDefault) || views[0];
 if (defaultView?.viewId) {
 navigate(`/${mod.moduleId}/${viewSlugForUrl(defaultView.viewId)}`);
} else {
 navigate(`/${mod.moduleId}`);
}
};

 // ── Explicit entitlement states ────────────────────────────────────────
 // Neither branch renders a single module card, name, or count. The
 // catalogue is only reachable once access has been positively resolved.
 if (moduleAccess.state === 'loading') {
 return (
 <div className="min-h-full bg-surface-base">
 <div className="mx-auto max-w-7xl px-6 py-6">
 <p className="t-eyebrow">Modules</p>
 <h1 className="mt-1 text-3xl font-semibold tracking-tight text-foreground">
 Checking your access…
 </h1>
 <div
 role="status"
 aria-live="polite"
 data-testid="browse-entitlements-loading"
 className="mt-6 flex items-center gap-3 text-sm text-muted-foreground"
 >
 <span className="h-4 w-4 animate-spin motion-reduce:animate-none rounded-full border-b-2 border-primary" />
 <span>Resolving which modules you're entitled to open.</span>
 </div>
 </div>
 </div>
 );
}

 if (moduleAccess.state === 'unavailable') {
 return (
 <div className="min-h-full bg-surface-base">
 <div className="mx-auto max-w-7xl px-6 py-6">
 <p className="t-eyebrow">Modules</p>
 <h1 className="mt-1 text-3xl font-semibold tracking-tight text-foreground">
 Access could not be verified
 </h1>
 <Card variant="elevated" className="mt-6 max-w-2xl">
 <CardContent className="p-6">
 <div
 role="alert"
 data-testid="browse-entitlements-unavailable"
 className="text-sm text-muted-foreground"
 >
 <p className="font-medium text-foreground">
 The entitlement service didn't answer.
 </p>
 <p className="mt-1">
 Tyndo can't confirm which modules you're allowed to open, so none are
 listed. Retry from the navigation panel, or contact your administrator
 if this persists.
 </p>
 </div>
 </CardContent>
 </Card>
 </div>
 </div>
 );
}

 return (
 <div className="min-h-full bg-surface-base">
 <div className="mx-auto max-w-7xl px-6 py-6">
 {/* Header */}
 <div className="mb-6 flex items-end justify-between">
 <div>
 <p className="t-eyebrow">Modules</p>
 <h1 className="mt-1 text-3xl font-semibold tracking-tight text-foreground">
 Browse {permittedModules.length} modules
 </h1>
 <p className="mt-1 text-sm text-muted-foreground">
 {greet(user?.name)} — everything you're entitled to open, grouped by ISA-95 layer.
 </p>
 </div>
 </div>

 {/* Stat ribbon */}
 <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
 <StatChip
 icon={Package}
 label="Equipment"
 value={fmt(equipmentCount)}
 sub="Tracked"
 />
 <StatChip
 icon={AlertTriangle}
 label="Active alarms"
 value={fmt(activeAlarmCount)}
 sub={activeAlarmCount === 0 ? 'All clear' : 'Tap to triage'}
 tone={activeAlarmCount > 0 ? 'critical' : 'phosphor'}
 onClick={
 activeAlarmCount > 0 && permittedModuleIdSet.has('tyndo.alarms')
 ? () => navigate('/tyndo.alarms')
 : undefined
}
 />
 <StatChip
 icon={ClipboardList}
 label="Work orders"
 value={fmt(activeWorkOrderCount)}
 sub="Released + In-Progress"
 onClick={
 permittedModuleIdSet.has('tyndo.production')
 ? () => navigate('/tyndo.production')
 : undefined
}
 />
 <StatChip
 icon={TrendingUp}
 label="Modules"
 value={permittedModules.length.toString()}
 sub="Available to you"
 tone="phosphor"
 />
 </div>

 {/* Adaptive dashboard — the persona's most-used PERMITTED surfaces,
 surfaced first. Only shown when there is real usage signal; every
 module here also appears in the full catalogue below, so nothing is
 hidden and the default order is always one scroll away. */}
 {mostUsed.length > 0 && (
 <section className="mb-7">
 <div className="mb-3 flex items-baseline gap-2">
 <Sparkles className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
 <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
 Your most-used
 </h2>
 <span className="text-[11px] text-muted-foreground/70">
 Ordered by your usage — reset anytime from the top bar
 </span>
 </div>
 <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
 {mostUsed.map((mod) => (
 <ModuleCard
 key={`mostused-${mod.moduleId}`}
 mod={mod}
 onClick={() => handleModuleClick(mod)}
 />
 ))}
 </div>
 </section>
 )}

 {/* Grouped module catalogue */}
 <div className="space-y-7">
 {ISA_GROUP_ORDER.map((g) => {
 const list = grouped.get(g) ?? [];
 if (list.length === 0) return null;
 return (
 <section key={g}>
 <div className="mb-3 flex items-baseline justify-between">
 <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
 {ISA_GROUP_LABEL[g as keyof typeof ISA_GROUP_LABEL]}
 </h2>
 <span className="t-tabular text-[11px] text-muted-foreground/70">
 {list.length} module{list.length === 1 ? '' : 's'}
 </span>
 </div>
 <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
 {list.map((mod) => (
 <ModuleCard
 key={mod.moduleId}
 mod={mod}
 onClick={() => handleModuleClick(mod)}
 />
 ))}
 </div>
 </section>
 );
})}
 {permittedModules.length === 0 && (
 <Card variant="elevated">
 <CardContent className="p-8 text-center">
 {modules.length === 0 ? (
 <p className="text-sm text-muted-foreground">
 No modules registered yet. Check that the gateway is serving
 <code className="t-mono mx-1 rounded bg-muted px-1.5">
 /api/v1/ui/manifest
 </code>
 and refresh.
 </p>
 ) : (
 <p data-testid="browse-no-entitled-modules" className="text-sm text-muted-foreground">
 You aren't entitled to any modules in this workspace. Ask your
 administrator to grant access.
 </p>
 )}
 </CardContent>
 </Card>
 )}
 </div>
 </div>
 </div>
 );
}

// ─── Subcomponents ─────────────────────────────────────────────────────

function ModuleCard({
 mod,
 onClick,
}: {
 mod: ModuleManifestEntry;
 onClick: () => void;
}) {
 const Icon = resolveIcon(mod.icon) ?? Package;
 const moduleSlug = mod.moduleId.replace(/^tyndo\./,'');
 return (
 <Button
 type="button"
 variant="ghost"
 onClick={onClick}
 className="h-auto whitespace-normal w-full justify-start text-left font-normal items-stretch group relative flex h-full flex-col gap-3 rounded-lg border border-border bg-surface-raised p-4 text-left transition-all hover:border-primary/40 hover:shadow-[var(--shadow-md)]"
 >
 <div className="flex items-start justify-between">
 <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary transition-colors group-hover:bg-primary/15">
 <Icon className="h-4.5 w-4.5" />
 </span>
 <ChevronRight className="h-4 w-4 text-muted-foreground/50 transition-all group-hover:translate-x-0.5 group-hover:text-foreground" />
 </div>
 <div className="flex-1">
 <h3 className="text-sm font-semibold text-foreground leading-tight">
 {mod.name}
 </h3>
 <p className="t-mono text-[10px] text-muted-foreground/60 mt-0.5">
 {moduleSlug}
 </p>
 </div>
 </Button>
 );
}

function StatChip({
 icon: Icon,
 label,
 value,
 sub,
 tone ='default',
 onClick,
}: {
 icon: LucideIcon;
 label: string;
 value: string;
 sub: string;
 tone?:'default' |'phosphor' |'critical';
 onClick?: () => void;
}) {
 const accent =
 tone === 'phosphor'
 ? 'hsl(var(--data-phosphor))'
 : tone === 'critical'
 ? 'hsl(var(--alarm-critical))'
 : 'hsl(var(--muted-foreground))';
 return (
 <Button
 type="button"
 variant="ghost"
 onClick={onClick}
 disabled={!onClick}
 className="h-auto whitespace-normal w-full justify-start text-left font-normal items-stretch disabled:pointer-events-auto disabled:opacity-100 group relative flex flex-col gap-1.5 rounded-lg border border-border bg-surface-raised p-4 text-left transition-all hover:shadow-sm disabled:cursor-default"
 >
 <span
 aria-hidden
 className="absolute inset-x-0 top-0 h-0.5 rounded-t-lg"
 style={{ backgroundColor: accent}}
 />
 <div className="flex items-center justify-between">
 <p className="t-eyebrow">{label}</p>
 <Icon className="h-4 w-4" style={{ color: accent}} />
 </div>
 <div className="t-display text-foreground">{value}</div>
 <div className="t-tabular text-xs text-muted-foreground">{sub}</div>
 </Button>
 );
}

function resolveIcon(name?: string): LucideIcon | undefined {
 if (!name) return undefined;
 return (icons as Record<string, LucideIcon>)[name];
}

function fmt(n: number | null | undefined): string {
 return typeof n ==='number' ? n.toLocaleString(resolveActiveLocale()) :'—';
}

function greet(name?: string): string {
 const h = new Date().getHours();
 const greeting =
 h < 5 ? 'Up late' : h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
 return name ? `${greeting}, ${name.split('')[0]}` : greeting;
}
